/**
 * TikTok pixel events that need runtime data.
 *
 * AddToCart rides the cart-update event rather than the form submit, so it
 * fires on a confirmed add and never on one that failed. Only the product
 * form adds items — every other cart-update source is a quantity change or a
 * refresh, which TikTok does not want reported as an add.
 *
 * ViewContent is emitted from `snippets/tiktok-pixel.liquid`, Purchase from
 * the checkout web pixel (see `docs/tiktok-purchase-web-pixel.js`).
 */

import { ThemeEvents } from '@theme/events';

const ADD_TO_CART_SOURCE = 'product-form';

/**
 * Build one TikTok `contents` entry from a Shopify cart line item.
 * @param {Object} lineItem
 */
function toContent(lineItem) {
  return {
    content_id: String(lineItem.variant_id || lineItem.id || ''),
    content_type: 'product',
    content_name: lineItem.product_title || lineItem.title || '',
    price: (lineItem.final_price ?? lineItem.price ?? 0) / 100,
    quantity: lineItem.quantity || 1
  };
}

/**
 * `/cart/add.js` answers with a single line item when the form posts one
 * variant, and with `{ items: [...] }` when it posts several.
 * @param {Object} response
 * @returns {Object[]}
 */
function addedItems(response) {
  if (!response) return [];
  if (Array.isArray(response.items)) return response.items;
  return response.variant_id || response.id ? [response] : [];
}

function trackAddToCart(event) {
  if (event.detail?.data?.source !== ADD_TO_CART_SOURCE) return;
  if (typeof window.ttq === 'undefined') return;

  const contents = addedItems(event.detail.resource).map(toContent);
  if (!contents.length) return;

  const value = contents.reduce((total, content) => total + content.price * content.quantity, 0);

  window.ttq.track('AddToCart', {
    contents,
    value: Math.round(value * 100) / 100,
    currency: window.Shopify?.currency?.active || window.tiktokPixelCurrency || ''
  });
}

document.addEventListener(ThemeEvents.cartUpdate, trackAddToCart);
